var _ = require("lodash");
var { IdError } = require("../../../typing/typing");

var LambdaTermId = require("./LambdaTermId");
var Var = require("./Constructors").Var;
var Application = require("./Constructors").Application;
var Lambda = require("./Constructors").Lambda;
var External = require("./Constructors").External;
var renameVar = require("./Substitutions").renameVar;
var Externals = require("./Externals");

/** string markers used in 'type' field in JSON to identify a constructor. */
var Markers = {
  Var: "LambdaVar",
  Application: "LambdaApplication",
  Lambda: "LambdaLambda",
  External: "LambdaExternal"
};

/**
 * JSON representation of lambda terms.
 *
 * JSON representation of a lambda term is a JSON object with a mandatory
 * property `type` with a string value called a marker defining what constructor
 * it corresponds to.
 * Therefore, there are 4 possible values of `type` property:
 * `Markers.Var`, `Markers.Application`, `Markers.Lambda` and
 * `Markers.External` where `Markers` is a static collection of markers.
 * 
 * Every JSON lambda term has the same additional set of property
 * as corresponding lambda term in native representation.
 * For example, a variable may have a `varType` property,
 * a JSON lambda term representing a variable has a `type=Markers.Var`
 * and optional `varType` property.
 * Similarly, JSON representation of Application lambda term
 * has a `type=Markers.Application` and two additional properties
 * `func` and `arg` which values are JSON representations of corresponding
 * lambda terms
 *
 * 
 *
 * @typedef LambdaJSON
 * @global
 * @see module:Lambda/JSON~Markers
 */

/**
 * convert JSON representation to the native one.
 *
 * JSON representation uses type markers: each object must have a 'type' field.
 * All the types are listed in Markers.
 *
 * A variable is represented by { type: Markers.Var, name: "some string" }
 * A variable representation might have optional field 'varType'
 *   that specifies a type of the variable.
 *
 * An application is { type: Markers.Application, body, arg }
 *
 * A lambda-abstraction is represented by
 *   {
 *     type: Markers.Lambda,
 *     arg: { type: Markers.Var, name: "x", varType: type },
 *     body: json
 *   }
 * @summary import lambda term from JSON.
 * @param {LambdaJSON} expr - a JSON representation to parse
 * @param {Object} env - an environment
 * @return {LambdaTerm} a parsed lambda term
 * @see module:Lambda/Externals~ImportFunction
 */
function importFromJSON(expr, env) {
  
  //LambdaJSONId(expr);
  
  if(env === undefined) env = {};
    
  // strings are short forms of untyped variables
  if(_.isString(expr))
    expr = { type: Markers.Var, name: expr };
  
  switch(expr.type) {
  
    case Markers.Var:
      if(env[expr.name] === undefined)
        return env[expr.name] = new Var(expr.varType);
      else {
        //TODO: check an equality of env[expr.name].varType and expr.varType
        return env[expr.name];
      }
      
    case Markers.Application:
      return new Application(
          importFromJSON(expr.func, env),
          importFromJSON(expr.arg, env));
      
    case Markers.Lambda:
      var name    = _.isString(expr.arg) ? expr.arg  : expr.arg.name;
      var varType = _.isString(expr.arg) ? undefined : expr.arg.varType;
      /* well, these two cases might be combined */
      if(env[name] === undefined) {
        env[name] = new Var(varType);
        var res = new Lambda(env[name], importFromJSON(expr.body, env));
        delete env[expr.arg];
      } else {
        var v = env[expr.arg];
        env[expr.arg] = new Var(varType);
        var res = new Lambda(env[expr.arg], importFromJSON(expr.body, env));
        env[expr.arg] = v;
      }
      return res;
      
    case Markers.External:
      var parse = Externals.getExternalImport(expr.objType);
      var obj = parse(expr.object, env, importFromJSON);
      return new External(obj, expr.objType, expr.termType);
  
  }
  
  throw "unknown LambdaJSON type '" + expr.type + "'";

}

// wrap it with typechecking
//importFromJSON = FunctionId(importFromJSON, LambdaJSONId, LambdaTermId);


/**
 * setName(v, prefered, forbidden) sets a string field 'name'.
 * If 'name' is already defined, nothing happens, even if 'forbidden' contains it.
 * Otherwise, the last string in prefered array that does not occur in forbidden array is used. In that case all the elements up to selected are dropped.
 * If no appropriate string is found, random string is used. 'Prefered' array is empty, 'forbidden' array extends with that string.
 * Generated string is garateed not to occur in 'forbidden'.
 * @summary generate a string name of variable.
 * @param v - arbitrary object
 * @param prefered - an array of strings or undefined
 * @param forbidden - an array of strings or undefined
 * @return undefined
 * @sideeffect
 *   if undefined, v.name is set to the first prefered non-forbidden string;
 *   suffix of 'prefered' containing string of 'forbidden' is removed,
 *   the chosed string for v.name is also removed from 'prefered';
 *   'forbidden' array extends with non-prefered name if used
 * @private
 */
function setName(v, prefered, forbidden) {
  if(v.name !== undefined) return;
  if(prefered !== undefined && prefered.length > 0) {
    do v.name = prefered.pop(); while(_.includes(forbidden, v.name));
  }
  if(v.name === undefined) {
    var k = 0;
    do v.name = "x" + (++k); while(_.includes(forbidden, v.name));
  }
  if(forbidden !== undefined) forbidden.push(v.name);
}

/**
 * convert a lambda term to JSON format.
 *
 * Two optional argumenrs `prefered` and `forbidden` are arrays of strings.
 * They specify names of bound variables that are prefered or forbidden to use.
 * 
 * Each unbound variable must have a field `name` and, optionally, `varType`.
 * If they don't, `name` field will be appended.
 * All the other variables will not be affected.
 *
 * @param {LambdaTerm} term - a lambda term
 * @param {string[]} prefered - an array of prefered names for bound variables
 * @param {string[]} forbidden - an array of strings that cannot be used
 *   as names for bound variables
 * @return {LambdaJSON} a JSON representation of `term`
 * @see module:Lambda/Externals~ExportFunction
 */
function exportToJSON(term, prefered, forbidden) {
  if(prefered  == undefined) prefered  = [];
  if(forbidden == undefined) forbidden = [];
  // term is already converted
  if(term instanceof Var) {
    setName(term, prefered, forbidden);
    return _.assign({ type: Markers.Var }, term);
  }
  //
  if(term instanceof Application)
    return {
      type: Markers.Application,
      func: exportToJSON(term.func, prefered, forbidden),
      arg:  exportToJSON(term.arg,  prefered, forbidden)
    };
  //
  if(term instanceof Lambda) {
    var v = new Var(term.arg.varType);
    setName(v, prefered, forbidden);
    var body = renameVar(term.body, term.arg, v);
    return {
      type: Markers.Lambda,
      arg: _.assign({ type: Markers.Var }, v),
      body: exportToJSON(body, prefered, forbidden)
    };
  }
  //
  if(term instanceof External) {
    var e = Externals.getExternalExport(term.objType);
    if(e === undefined) console.error(term);
    return {
      type: Markers.External,
      object: e(term.object, prefered, forbidden, exportToJSON),
      objType: term.objType,
      termType: term.termType
    };
  }
  console.error(term);
  throw "not a lambda term";
}



/**
 * convert JSON representation of lambda term to string.
 *
 * Rules are default:
 * - each variable is written with its name,
 * - application's parts are juxtaposed,
 * - lambda abstraction is of form `"\x.M"`, `x` is a var's name, `M` is a body.
 * @see module:Lambda/JSON~exportToJSON
 *
 * @param {LambdaJSON} json - JSON representation of lambda term to convert
 * @param {boolean} [braced=false] - parenthesize the whole expression or not.
 *   Set that parameter to `true` if that expression is a substring of
 *   another one.
 * @return a string
 */
function JSONToString(json, braced) {
  switch(json.type) {
    case Markers.Var:
      return json.name;
    case Markers.Application:
      var r = JSONToString(json.func, json.func.type == Markers.Lambda) + " " + JSONToString(json.arg, true);
      return braced ? "(" + r + ")" : r;
    case Markers.Lambda:
      var r = "\\" + json.arg.name + "." + JSONToString(json.body, false);
      return braced ? "(" + r + ")" : r;
    default:
      return "<…>";
  }
}


/**
 * LambdaJSONId.
 * @member
 */
function LambdaJSONId(json) {
  if(json === undefined)
    throw new IdError("object undefined", json, LambdaJSONId);
  if(! _.isObject(json))
    throw new IdError("not an object", json, LambdaJSONId);
  switch(json.type) {
    case Markers.Var:
      if(! _.isString(json.name)) {
        var e = new IdError("anonymous variable", json, LambdaJSONId);
        e.path = [];
        throw e;
      }
      return json;
    case Markers.Application:
      try {
        LambdaJSONId(json.func);
      } catch(e) {
        e.path = _.concat(["func", e.path]);
        throw e;
      }
      try {
        LambdaJSONId(json.arg);
      } catch(e) {
        e.path = _.concat(["arg", e.path]);
        throw e;
      }
      return json;
    case Markers.Lambda:
      if(! _.isString(json.arg) &&
        (! _.isObject(json.arg) || ! _.isString(json.arg.name))) {
        var e = new IdError("formal argument is not a variable", json, LambdaJSONId);
        e.path = [];
        throw e;
      }
      try {
        LambdaJSONId(json.body);
      } catch(e) {
        e.path = _.concat(["func", e.path]);
        throw e;
      }
      return json;
    case Markers.External:
      console.warn("skipping");
      return json;
      var ext = Externals.ExternalObjectInterpretation[json.objType];
      if(ext === undefined || ext.id === undefined)
        return json;
      try {
        return ext.id(json.object);
      } catch(err) {
        var e = new IdError("Bad format of external object", json, LambdaJSONId);
        e.err = err;
        e.path = [];
        throw e;
      }
  }
  var msg = "unknown type " + json.type + " of JSON lambda term";
  throw new IdError(msg, json, LambdaJSONId);
};

LambdaJSONId.typeName = "Lambda JSON term";


/**
 * predicate of lambda JSON represenation.
 * Only top-level attributes are checked.
 * @param {*} obj - any JS expression
 * @return {boolean}
 */
function isLambdaJSON(obj) {
  return obj && _.includes(Markers, obj.type);
}

// JSON module depends on Predicates modules
// and it would be natural to define isLambdaJSON here.
// However, one might find it more convenient to place it in Predicates
// That is why a link is exported manually.
require("./Predicates").isLambdaJSON = isLambdaJSON;

module.exports = {
  __private__: { setName: setName },
  Markers: Markers,
  importFromJSON: importFromJSON,
  exportToJSON: exportToJSON,
  JSONToString: JSONToString,
  isLambdaJSON: isLambdaJSON,
  LambdaJSONId: LambdaJSONId
};
